import { API_BASE_URL } from '../config';
import axios from 'axios';
import { Artist, Album } from '@/types';

export interface PublicPinnedMixtape {
  id: string;
  position: number;
  mixtape: {
    id: string;
    title: string;
    description?: string | null;
    artwork_url?: string | null;
    follower_count?: number | null;
    cover_type?: string | null;
    cover_config?: Record<string, any> | null;
  } | null;
}

// Fetch artist details
export async function fetchArtist(artistId: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/public/artist/${artistId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch artist details');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching artist:', error);
    throw error;
  }
}

// Fetch artist albums - using public endpoint WITHOUT tracks (tracks fetched separately with auth)
export async function fetchArtistAlbums(artistId: string) {
  try {
    // Use public endpoint for album metadata only (no tracks)
    const response = await fetch(`${API_BASE_URL}/public/album?artist=${artistId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch artist albums');
    }
    const data = await response.json();
    // Return albums without tracks - tracks should be fetched separately using bundle endpoint
    return data.albums || [];
  } catch (error) {
    console.error('Error fetching artist albums:', error);
    throw error;
  }
}

// Fetch artist top tracks - using authenticated bundle endpoint
export async function fetchArtistTopTracks(artistId: string, limit = 10) {
  try {
    const token = localStorage.getItem("access_token");
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/bundle/artist/${artistId}/top-tracks?limit=${limit}`, {
      headers,
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch artist tracks');
    }
    
    const data = await response.json();
    // Bundle endpoint returns { bundle_type, artist_id, tracks }
    return {
      tracks: data.tracks || [],
      ...data
    };
  } catch (error) {
    console.error('Error fetching artist tracks:', error);
    throw error;
  }
}

// Search artists
export async function searchArtists(query: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/public/artist/search?name=${encodeURIComponent(query)}`);
    if (!response.ok) {
      throw new Error('Failed to search artists');
    }
    return await response.json();
  } catch (error) {
    console.error('Error searching artists:', error);
    throw error;
  }
}

// Get featured artists
export async function getFeaturedArtists(limit = 10) {
  try {
    const response = await fetch(`${API_BASE_URL}/public/artist?featured_artist=true&limit=${limit}`);
    if (!response.ok) {
      throw new Error('Failed to fetch featured artists');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching featured artists:', error);
    throw error;
  }
} 

export const getArtistById = async (id: string) => {
  const response = await axios.get(`${API_BASE_URL}/public/artist/${id}`);
  return response.data;
};

export const getAlbumsByArtistId = async (artistId: string): Promise<Album[]> => {
  try {
    if (!artistId) {
      console.warn('getAlbumsByArtistId called with invalid artistId');
      return [];
    }

    // Use the public artist endpoint which already includes albums
    const response = await axios.get(`${API_BASE_URL}/public/artist/${artistId}`);
    
    // Get the albums from the artist response
    if (!response.data || !response.data.albums || !Array.isArray(response.data.albums)) {
      console.warn(`getAlbumsByArtistId: Invalid response for artist ${artistId}`, response.data);
      return [];
    }
    
    return response.data.albums;
  } catch (error) {
    console.error(`Error in getAlbumsByArtistId for artist ${artistId}:`, error);
    return []; // Return empty array instead of throwing
  }
};

export const fetchArtistPinnedMixtapesBySlug = async (slug: string): Promise<PublicPinnedMixtape[]> => {
  const response = await fetch(`${API_BASE_URL}/public/artist/slug/${slug}/mixtapes`);
  if (!response.ok) {
    throw new Error('Failed to fetch pinned mixtapes');
  }
  const data = await response.json();
  return data.pins || [];
};
